import clsx from "clsx";
import { get, isNil } from "lodash-es";
import { type FC, useContext, useEffect, useMemo, useState } from "react";
import { Doc } from "sharedb/lib/client";

import { LINKED_SECTION_SEEDING_ACCESS_ID } from "../../shared/access-id";
import { AUTH_ERRORS } from "../../shared/types/websocket-auth";
import { AccessIdDataType, WorksheetType } from "../../shared/types/worksheet";
import { ACCESS_ID_WRAPPER, DATA_PATH } from "../../shared/worksheets/constants";
import { hasEditableBlocks } from "../../shared/worksheets/doc-helpers";

import { Blocks } from "./Blocks";
import { SubmissionWorksheet } from "./SubmissionWorksheet";
import { WorksheetResponses } from "./WorksheetResponses";

import { ErrorScreen, LoadingTextIndicator } from "components/LoadingScreen";
import { WAITED_TOO_LONG_TIMEOUT_MS } from "components/constants";
import { t } from "i18n/i18n";
import { Banner } from "mds/components/Banner";
import { DocViewContext, type PageTabVariant } from "providers/DocViewProvider";
import { store, useAppSelector } from "store/index";
import {
  selectCurrentSectionIsLinked,
  selectIsTimeline,
  selectWorksheetIsRefreshing,
} from "store/selectors";
import { BROADCAST_TOPICS, addBroadcastListener } from "utils/broadcast";
import { rollbarAndLogError } from "utils/logger";

type WorksheetProps = {
  doc: Doc<WorksheetType>;
};

export const Worksheet: FC<WorksheetProps> = ({ doc }) => {
  const { accessId, readOnly, pageId, canAuthorPage } = useContext(DocViewContext);
  const isTimeline = useAppSelector(selectIsTimeline);
  const isLinkedSection = useAppSelector(selectCurrentSectionIsLinked);

  const worksheetIsRefreshing = useAppSelector(selectWorksheetIsRefreshing);

  // accessIdData may be undefined if either:
  // - The user/group has never been on the page and is waiting for initialization
  // - The page is readonly, and we haven't initialized the data on a previous visit.
  const accessIdPath = useMemo(() => (accessId ? [DATA_PATH, accessId] : [DATA_PATH]), [accessId]);
  const accessIdData = get(doc.data, accessIdPath) as AccessIdDataType;

  // This determines whether we initialize the access ID wrapper, which is a per-user/group
  // subdirectory in which field data will be saved later on. At creation time of a worksheet,
  // we don't know which users will be on it (and need wrapper objects), so we create these
  // dynamically. In read-only cases, we don't need to do this, since fields won't need to save data.
  const [initError, setInitError] = useState<string | null>(null);
  const needsAccessIdWrapperInit =
    !initError && !readOnly && !accessIdData && hasEditableBlocks(doc) && !worksheetIsRefreshing;

  // if this is the first time a user from this access_id is viewing the worksheet, we need to initialize their access
  // ID wrapper in the doc so that data for block content can be added to it.
  useEffect(() => {
    // This might run with a stale version of worksheetIsRefreshing so
    // we need to check the store state again.
    const isRefreshing = selectWorksheetIsRefreshing(store.getState());
    const docReady = Boolean(doc.data) && Boolean(doc.type) && doc.version != null;
    if (!needsAccessIdWrapperInit || isRefreshing || !docReady) {
      return;
    }

    // If we somehow take longer than three seconds to do this, assume it's a backend issue,
    // and give the user a chance to retry.
    const timeout = setTimeout(() => {
      setInitError("Timeout");
      rollbarAndLogError("Access ID Wrapper init error timeout", {
        err: "useEffect timeout",
        path: accessIdPath.join("."),
        docId: doc.id,
      });
    }, WAITED_TOO_LONG_TIMEOUT_MS);

    let cancelled = false;
    // Re-check before init: permission refresh may have populated this path.
    const latestAccessIdData = get(doc.data, accessIdPath) as AccessIdDataType;
    if (latestAccessIdData) {
      clearTimeout(timeout);
      return () => {
        cancelled = true;
        clearTimeout(timeout);
      };
    }

    doc.submitOp({ p: accessIdPath, oid: ACCESS_ID_WRAPPER }, {}, (err) => {
      clearTimeout(timeout);
      if (cancelled) return;
      if (err && err.message !== AUTH_ERRORS.OVERWRITE_DISALLOWED) {
        setInitError(err.message);
        rollbarAndLogError("Access ID Wrapper init error", err);
      }
    });

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [accessIdPath, doc, needsAccessIdWrapperInit]);

  const seededAccessIdData = get(doc.data, [
    DATA_PATH,
    LINKED_SECTION_SEEDING_ACCESS_ID,
  ]) as AccessIdDataType;
  const needsSeedingAccessIdWrapperInit =
    canAuthorPage && isLinkedSection && !seededAccessIdData && !readOnly && !worksheetIsRefreshing;

  // if this is an instructor on a worksheet with seeded content who has never visited before,
  // we need to initialize the access ID wrapper for the seeding access ID so that seeded content can be saved in the doc
  useEffect(() => {
    // This might run with a stale version of worksheetIsRefreshing so
    // we need to check the store state again.
    const isRefreshing = selectWorksheetIsRefreshing(store.getState());
    const docReady = Boolean(doc.data) && Boolean(doc.type) && doc.version != null;
    if (!needsSeedingAccessIdWrapperInit || isRefreshing || !docReady) {
      return;
    }

    const seededAccessIdPath = [DATA_PATH, LINKED_SECTION_SEEDING_ACCESS_ID];

    // If we somehow take longer than three seconds to do this, assume it's a backend issue,
    // and give the user a chance to retry.
    const timeout = setTimeout(() => {
      setInitError("Timeout");
      rollbarAndLogError("Seeded Access ID Wrapper init error timeout", {
        err: "useEffect timeout",
        path: seededAccessIdPath.join("."),
        docId: doc.id,
      });
    }, WAITED_TOO_LONG_TIMEOUT_MS);

    doc.submitOp({ p: seededAccessIdPath, oid: ACCESS_ID_WRAPPER }, {}, (err) => {
      clearTimeout(timeout);

      if (err && err.message !== AUTH_ERRORS.OVERWRITE_DISALLOWED) {
        setInitError(err.message);
        rollbarAndLogError("Seeded Access ID Wrapper init error", err);
      }
    });

    return () => {
      clearTimeout(timeout);
    };
  }, [doc, needsSeedingAccessIdWrapperInit]);

  const [showShine, setShowShine] = useState(false);
  useEffect(() => {
    return addBroadcastListener(BROADCAST_TOPICS.AI_PAGE_ENHANCED, (message) => {
      if (message.pageId === pageId) {
        setShowShine(true);
        // Reset after animation completes
        setTimeout(() => setShowShine(false), 1500);
      }
    });
  }, [pageId]);

  // If an error happens during initialization, instead of re-trying automatically and possibly
  // getting stuck in a loop, allow for manual retries, and show a graceful message.
  if (initError) {
    return (
      <ErrorScreen retryText={t("worksheet.error.try_again")} onRetry={() => setInitError(null)}>
        <div className="body-m">{t("worksheet.error.access_id_init")}</div>
      </ErrorScreen>
    );
  }

  // Waiting to initialize student data path. At creation time of a worksheet, we don't
  // know which users will be filling it out, so the first time a new user wants to fill
  // in data, indicated by needsPathInitialization, we have the user submit an op that
  // initializes their individual user sub-directory in doc.data.d.
  // When viewing the worksheet as anyone else but an editing user, we don't need to
  // do this, since if the doc doesn't have the accessId initialized, that's fine,
  // we just show an empty worksheet.
  if (needsAccessIdWrapperInit) {
    return (
      <LoadingTextIndicator className="worksheet-column-wrapper">
        {t("worksheet.loading.access_id_init")}
      </LoadingTextIndicator>
    );
  }

  if (!accessIdData && isTimeline) {
    return <Banner kind="info">{t("worksheet.timeline.timeline_empty")}</Banner>;
  }

  return (
    <div
      className={clsx(
        "animate-shine-base worksheet-column-wrapper",
        showShine && "animate-shine-active",
      )}
    >
      {doc.data && <Blocks accessIdPath={accessIdPath} doc={doc} />}
    </div>
  );
};

type WorksheetFromShareDBDocProps = {
  doc: Doc<WorksheetType>;
  selectedTabVariant?: PageTabVariant;
  worksheetVersionNumber?: number;
};

export const WorksheetFromShareDBDoc: FC<WorksheetFromShareDBDocProps> = ({
  doc,
  selectedTabVariant,
  worksheetVersionNumber,
}) => {
  if (selectedTabVariant === "responses" && isNil(worksheetVersionNumber)) {
    return <WorksheetResponses doc={doc} />;
  }

  // TODO: SubmissionWorksheets should never be in rollup mode: we never show more than one
  //  submission at a time.
  // TODO: Submissions on rollup view look like a rollup now--we should make them look like
  //  submission worksheets again.
  if (worksheetVersionNumber) {
    return <SubmissionWorksheet doc={doc} worksheetVersionNumber={worksheetVersionNumber} />;
  }

  return <Worksheet doc={doc} />;
};
